import React, { useState, useEffect, useMemo, ReactElement } from 'react';
import { bindActionCreators } from 'redux';
import type { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import Moment from 'moment';
import { SearchBase } from '../../../../types/common';
import { DATE_FORMAT, MOBILE_WINDOW_BREAKPOINT, PROJECT_LOCATION_WAIVER_STATUSES } from '../../../../constants/constants';
import { CUSTOMER_ROLES } from '../../../../constants/roles';
import { actions as projectLocationActions } from '../../../../actions/projectLocationActions';
import { actions as projectManagerActions } from '../../../../actions/projectManagerActions';
import { actions as projectActions } from '../../../../actions/projectActions';
import * as types from '../../../../constants/actionTypes';
import Notification from '../../../Common/Notification';
import { getSearchParamSortFields, getDataTableSortFields } from '../../../../helpers/sortHelper';
import { createdFromNow } from '../../../../helpers/dateTime';
import ChangeZeroDateDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeZeroDateDrawer/ChangeZeroDateDrawer';
import ChangeProjectManagerDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeProjectManagerDrawer';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import Modal from '../../../Common/Modal/Modal';
import './ProjectWaivers.scss';

interface ProjectWaiversProps {
	projectId: number;
	projectKey: string;
	subscriptionId: string;
	windowWidth: number;
	customerRole: string;
}

interface ProjectWaiverSearchParams extends SearchBase {
	projectId: number;
	locationName: string;
	locationAbbreviation: string;
	waiverStatusId: number | null;
}

interface ProjectWaiverItem {
	isChecked: boolean;
	id: number;
	locationAbbreviation: string;
	locationName: string;
	requestorFirstName: string;
	requestorLastName: string;
	waiverStatusId: number;
	waiverRequestDate: Date;
	waiverResponseDate: Date | null;
}

const ProjectWaivers = ({ projectId, projectKey, windowWidth, subscriptionId, customerRole }: ProjectWaiversProps): ReactElement => {
	const [waivers, setWaivers] = useState<Array<ProjectWaiverItem>>([]);
	const [checkedRows, setCheckedRows] = useState<Array<any>>([]);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [allRowsChecked, setAllRowsChecked] = useState<boolean>(false);
	const [isSearching, setIsSearching] = useState<boolean>(true);
	const [searchParams, setSearchParams] = useState<ProjectWaiverSearchParams>({
		projectId: projectId,
		locationName: '',
		locationAbbreviation: '',
		waiverStatusId: null,
		pageSize: 25, // Smaller default page size for table in section of PM Dashboard
		pageNumber: 1,
		sortBy: 'requestedDate',
		sortDir: 'desc',
	});
	const [selectedWaiver, setSelectedWaiver] = useState<ProjectWaiverItem>();
	const [isChangeZeroDateDrawerOpen, setIsChangeZeroDateDrawerOpen] = useState<boolean>(false);
	const [showZeroDateWarning, setShowZeroDateWarning] = useState<boolean>(false);
	const [isChangeProjectManagerDrawerOpen, setIsChangeProjectManagerDrawerOpen] = useState<boolean>(false);
	const [showChangeManagerWarning, setShowChangeManagerWarning] = useState<boolean>(false);
	const [allProjectManagers, setAllProjectManagers] = useState<Array<any>>([]);
	const [isLoadingFormFilters, setIsLoadingFormFilters] = useState<boolean>(false);
	const [selectedBulkOption, setSelectedBulkOption] = useState<string>('');
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);

	const dispatch: Dispatch = useDispatch();
	const searchWaivers = async (params) => dispatch(projectLocationActions.waiverSearch(params));
	const approveDenyWaivers = async (params) => dispatch(projectLocationActions.waiverApproveDeny(params));
	const searchProjectManagers = async (params) => dispatch(projectManagerActions.searchProjectManagers(params));

	const bulkOperations = ['Approve', 'Not Approve'];
	const bulkOptions = bulkOperations.map((option) => ({
		label: option,
		value: option,
	}));

	const projectLocationActionCreators = useMemo(() => bindActionCreators(projectLocationActions, dispatch), [dispatch]);

	const projectActionCreators = useMemo(() => bindActionCreators(projectActions, dispatch), [dispatch]);

	useEffect(() => {
		const params = {
			...searchParams,
			projectId,
		};

		search(params);
	}, [projectId, searchTrigger]);

	const search = async (params: ProjectWaiverSearchParams) => {
		try {
			setIsSearching(true);

			const result = await searchWaivers(params);
			const payload = result.payload;
			if (result.type === types.SEARCH_PROJECTLOCATION_WAIVER_SUCCESS) {
				const newWaivers = payload.results.map((item) => {
					return { ...item, isChecked: false };
				});
				setWaivers(newWaivers);
				setTotalCount(payload.totalCount);
				setAllRowsChecked(false);
			} else if (result.type === types.SEARCH_PROJECTLOCATION_WAIVER_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const isCustomerAdmin = () => {
		return [CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Moderator.name, CUSTOMER_ROLES.Owner.name].includes(customerRole);
	};

	const onSort = ({ sortField, sortOrder }) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
		if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
			setSearchParams({
				...searchParams,
				sortBy: sortBy,
				sortDir: sortDir,
			});
		}
	};

	const onToggleCheckedRow = (waiverId: number) => {
		const theWaivers = waivers;
		const newCheckedRows: Array<any> = [];

		const newWaivers = theWaivers.map((item) => {
			const waiver = { ...item };
			if (item.id === waiverId) {
				waiver.isChecked = !item.isChecked;
			}

			if (waiver.isChecked) {
				newCheckedRows.push(waiver);
			}
			return waiver;
		});

		// If we've manually checked all tasks, also check the master checkbox
		const allRowsChecked = newWaivers.length === newCheckedRows.length;

		setCheckedRows(newCheckedRows);
		setWaivers(newWaivers);
		setAllRowsChecked(allRowsChecked);

		return newWaivers;
	};

	const onMasterCheckboxToggle = () => {
		// Toggle the master checkbox to checked or unchecked
		const newAllRowsChecked = !allRowsChecked;

		// Check or uncheck all rows, based on the master checkbox's newly-toggled state
		const newWaivers = waivers.map((item) => {
			return { ...item, isChecked: newAllRowsChecked };
		});
		const newCheckedRows = newAllRowsChecked ? newWaivers.slice() : [];

		setCheckedRows(newCheckedRows);
		setWaivers(newWaivers);
		setAllRowsChecked(newAllRowsChecked);
	};

	const onSearchBlur = () => {
		search(searchParams);
	};

	const keyUpFilter = (e) => {
		if (e.keyCode == 13) {
			search(searchParams);
		}
	};

	const filterChange = (e) => {
		setSearchParams({
			...searchParams,
			[e.target.name]: e.target.value,
		});
	};

	const onPageChange = async (e) => {
		const newSearchParams = { ...searchParams, pageNumber: e.page + 1 };

		setSearchParams(newSearchParams);
		await search(newSearchParams);
	};

	const onWaiverStatusChange = async (e) => {
		const newSearchParams = { ...searchParams, waiverStatusId: e.value };

		setSearchParams(newSearchParams);
		await search(newSearchParams);
	};

	const loadAllProjectManagers = async () => {
		const request = {
			isActive: true,
			name: '',
			subscriptionId: subscriptionId,
			pageSize: 0, //to get all results
			sortBy: 'Name',
		};

		try {
			const response = await searchProjectManagers(request);
			if (response.type === types.SEARCH_PROJECTMANAGER_SUCCESS) {
				setAllProjectManagers(response.payload.results);
			} else if (response.type === types.SEARCH_PROJECTMANAGER_FAILURE) {
				Notification('error', 'An Error Occurred', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	const applyBulkAction = async () => {
		switch (selectedBulkOption) {
			case 'Approve':
				await onApprove(waivers);
				break;
			case 'Not Approve':
				await onDeny(waivers);
				break;
			default:
				return;
		}

		setSelectedBulkOption('');
		clearChecked();
	};

	const onApprove = async (list) => {
		const theWaivers = list.filter((waiver) => waiver.isChecked == true);

		if (!theWaivers || theWaivers.length === 0) {
			Notification('error', '', 'You must have at least 1 waiver selected');
			return;
		}

		const waiverIds = theWaivers.map((waiver) => waiver.id);
		try {
			setIsSearching(true);
			const request = {
				projectKey: projectKey,
				projectLocationIds: waiverIds,
				approve: true,
			};

			const result = await approveDenyWaivers(request);
			const payload = result.payload;
			if (result.type === types.APPROVE_DENY_PROJECTLOCATION_WAIVER_SUCCESS) {
				Notification('success', '', 'Waiver(s) successfully approved');
				setSearchTrigger((prev) => !prev);
			} else if (result.type === types.APPROVE_DENY_PROJECTLOCATION_WAIVER_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const onDeny = async (list) => {
		const theWaivers = list.filter((waiver) => waiver.isChecked == true);

		if (!theWaivers || theWaivers.length === 0) {
			Notification('error', '', 'You must have at least 1 waiver selected');
			return;
		}

		const waiverIds = theWaivers.map((waiver) => waiver.id);
		try {
			setIsSearching(true);

			const request = {
				projectKey: projectKey,
				projectLocationIds: waiverIds,
				approve: false,
			};
			const result = await approveDenyWaivers(request);

			const payload = result.payload;
			if (result.type === types.APPROVE_DENY_PROJECTLOCATION_WAIVER_SUCCESS) {
				Notification('success', '', 'Waiver(s) successfully not approved');
				setSearchTrigger((prev) => !prev);
			} else if (result.type === types.APPROVE_DENY_PROJECTLOCATION_WAIVER_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setIsSearching(false);
		}
	};

	const onChangeManagerClick = async (rowData) => {
		setSelectedWaiver(rowData);
		await onChangeManager();
	};

	const onChangeZeroDateClick = async (rowData) => {
		setSelectedWaiver(rowData);
		setIsChangeZeroDateDrawerOpen(true);
	};

	const onApproveSingle = async (rowId) => {
		const theWaivers = onToggleCheckedRow(rowId);

		await onApprove(theWaivers);

		clearChecked();
	};

	const onDenySingle = async (rowId) => {
		const theWaivers = onToggleCheckedRow(rowId);

		await onDeny(theWaivers);

		clearChecked();
	};

	const clearChecked = () => {
		// Uncheck all rows
		const uncheckedWaivers = waivers.map((item) => {
			return { ...item, isChecked: false };
		});

		setWaivers(uncheckedWaivers);
		setCheckedRows([]);
		setAllRowsChecked(false);
	};

	const onChangeZeroDateComplete = (showZeroDateWarning: boolean) => {
		setShowZeroDateWarning(showZeroDateWarning);
		setIsChangeZeroDateDrawerOpen(false);
	};

	const onChangeZeroDateCancel = () => {
		setIsChangeZeroDateDrawerOpen(false);
	};

	const onChangeManager = async () => {
		// only get PMs if the array is empty so we only need to load them the first time the drawer is opened
		if (allProjectManagers.length === 0) {
			setIsChangeProjectManagerDrawerOpen(true);
			setIsLoadingFormFilters(true);
			await loadAllProjectManagers();
			setIsLoadingFormFilters(false);
		} else {
			setIsChangeProjectManagerDrawerOpen(true);
		}
	};

	const onChangeManagerComplete = (showChangeManagerWarning: boolean) => {
		setShowChangeManagerWarning(showChangeManagerWarning);
		setIsChangeProjectManagerDrawerOpen(false);
	};

	const onChangeManagerCancel = () => {
		setIsChangeProjectManagerDrawerOpen(false);
	};

	const checkboxTemplate = (rowData) => {
		return <Checkbox name='checkbox' onChange={() => onToggleCheckedRow(rowData.id)} checked={rowData.isChecked}></Checkbox>;
	};

	const nameTemplate = (rowData) => {
		return <div className='location-name'>{rowData.locationName}</div>;
	};

	const waiverStatusTemplate = (rowData) => {
		return (
			<div className='waiver-status'>
				{rowData.waiverStatusId === PROJECT_LOCATION_WAIVER_STATUSES.Approved ? (
					<div className='status-icon approved'></div>
				) : rowData.waiverStatusId === PROJECT_LOCATION_WAIVER_STATUSES.Denied ? (
					<div className='status-icon denied'></div>
				) : (
					<div className='status-icon pending'></div>
				)}
				{rowData.waiverStatusId === PROJECT_LOCATION_WAIVER_STATUSES.Approved ? (
					<span className='waiver-status-name'>Approved</span>
				) : rowData.waiverStatusId === PROJECT_LOCATION_WAIVER_STATUSES.Denied ? (
					<span className='waiver-status-name'>Not Approved</span>
				) : (
					<span className='waiver-status-name'>Pending</span>
				)}
			</div>
		);
	};

	const requestedDateTemplate = (rowData) => {
		return Moment.utc(rowData.waiverRequestDate).format(DATE_FORMAT);
	};

	const requestedFromNowTemplate = (rowData) => {
		return createdFromNow(rowData.waiverRequestDate);
	};

	const requestedByTemplate = (rowData) => {
		return `${rowData.requestorFirstName} ${rowData.requestorLastName}`;
	};

	const responseDateTemplate = (rowData) => {
		const actionIcons = (
			<div className='action-icons'>
				<i className='far fa-user-pen' title='Change Manager' onClick={() => onChangeManagerClick(rowData)} />
				<i className='far fa-calendar-lines-pen' title='Change Zero Date' onClick={() => onChangeZeroDateClick(rowData)} />
				<i className='far fa-circle-check' title='Approve' onClick={() => onApproveSingle(rowData.id)} />
				<i className='far fa-times-circle' title='Not Approve' onClick={() => onDenySingle(rowData.id)} />
			</div>
		);

		if (rowData.waiverResponseDate) {
			return (
				<>
					<div className='response-date'>
						<div>{Moment.utc(rowData.waiverResponseDate).format(DATE_FORMAT)}</div>
					</div>
					<div className='waiver-actions'>{actionIcons}</div>
				</>
			);
		} else {
			return <div className='waiver-actions'>{actionIcons}</div>;
		}
	};

	const displayColumns = windowWidth < MOBILE_WINDOW_BREAKPOINT ? 'none' : '';

	const nameFilterElement = (
		<div>
			<input
				type='text'
				name='locationName'
				className='ui-column-filter p-inputtext'
				placeholder='Filter by Name'
				size={50}
				onBlur={onSearchBlur}
				onChange={filterChange}
				onKeyUp={keyUpFilter}
				value={searchParams.locationName}
			/>
		</div>
	);

	const abbreviationFilterElement = (
		<div>
			<input
				type='text'
				name='locationAbbreviation'
				className='ui-column-filter p-inputtext'
				placeholder='Filter by Abbreviation'
				size={50}
				onBlur={onSearchBlur}
				onChange={filterChange}
				onKeyUp={keyUpFilter}
				value={searchParams.locationAbbreviation}
			/>
		</div>
	);

	const masterCheckbox = <Checkbox name='checkbox' onChange={() => onMasterCheckboxToggle()} checked={allRowsChecked}></Checkbox>;

	const { sortField, sortOrder } = getDataTableSortFields(searchParams);

	const waiverStatusOptions = [
		{ label: 'Pending', value: 1 },
		{ label: 'Approved', value: 2 },
		{ label: 'Not Approved', value: 3 },
	];

	const waiverStatusFilterElement = (
		<Dropdown className='ui-column-filter' value={searchParams.waiverStatusId} options={waiverStatusOptions} onChange={onWaiverStatusChange} />
	);

	const zeroDateWarningModalBody =
		'Some locations were not updated because the project has not been pushed to them. Before changing Zero Date, consider filtering the Pushed Down column to a value of Yes.';
	const zeroDateWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={() => setShowZeroDateWarning(false)}>
				Ok
			</button>
		</div>
	);

	const managerWarningModalBody =
		'Some locations were not updated because the project has not been pushed to them. Before changing managers, consider filtering the Pushed Down column to a value of Yes.';
	const managerWarningModalFooter = (
		<div>
			<button type='button' className='btn btn-primary' data-dismiss='modal' onClick={() => setShowChangeManagerWarning(false)}>
				Ok
			</button>
		</div>
	);

	const header = (
		<div className='table-header-container'>
			{isCustomerAdmin() && (
				<div className='row'>
					<div className='col-md-12'>
						<div className='btn-group'>
							<Dropdown
								className='ui-column-filter'
								value={selectedBulkOption}
								options={bulkOptions}
								onChange={(e) => setSelectedBulkOption(e.value)}
								style={{
									width: '250px',
									fontFamily: 'GreyCliff',
									borderRadius: '6px',
									fontSize: '14px',
								}}
								placeholder='Bulk Actions'
							/>
							<button
								type='button'
								className='btn pacer61-purple-btn ms-3'
								disabled={checkedRows.length === 0}
								onClick={applyBulkAction}>
								Apply
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);

	return (
		<div className='project-waiver-section'>
			<LoadingIndicator isLoading={isSearching} hideWhileLoading={true}>
				<div>
					<div className='identifier-results-table'>
						<DataTable
							filterDisplay='row'
							className='p-datatable-sm p-datatable-striped p-datatable-gridlines'
							value={waivers}
							sortField={sortField}
							sortOrder={sortOrder}
							onSort={onSort}
							header={header}>
							<Column
								className='checkbox-column'
								field='isChecked'
								filter={true}
								filterElement={masterCheckbox}
								body={checkboxTemplate}
								style={{ textAlign: 'center', width: '40px' }}
							/>
							<Column
								field='waiverStatusId'
								header='Waiver Status'
								sortable={true}
								body={waiverStatusTemplate}
								filter={true}
								filterElement={waiverStatusFilterElement}
							/>
							<Column
								field='waiverRequestDate'
								header='Created'
								sortable={true}
								body={requestedDateTemplate}
								style={{ width: '105px', display: displayColumns }}
							/>
							<Column
								field='requestedBy'
								header='Requestor'
								sortable={true}
								body={requestedByTemplate}
								style={{ display: displayColumns }}
							/>
							<Column
								field='locationName'
								header='Location'
								sortable={true}
								body={nameTemplate}
								filter={true}
								filterElement={nameFilterElement}
							/>
							<Column
								field='locationAbbreviation'
								header='Abbreviation'
								sortable={true}
								filter={true}
								filterElement={abbreviationFilterElement}
								style={{ display: displayColumns }}
							/>
							<Column
								field='requestedFromNow'
								header='Time'
								body={requestedFromNowTemplate}
								sortable={true}
								style={{ display: displayColumns }}
							/>
							<Column
								field='waiverResponseDate'
								header='Response Date'
								sortable={true}
								body={responseDateTemplate}
								style={{ width: '175px', display: displayColumns }}
							/>
						</DataTable>
						{totalCount > searchParams.pageSize && (
							<Paginator
								rows={searchParams.pageSize}
								totalRecords={totalCount}
								first={(searchParams.pageNumber - 1) * searchParams.pageSize}
								onPageChange={onPageChange}
							/>
						)}
						<div className='results-count'>Total records: {totalCount}</div>
					</div>
				</div>

				{isChangeZeroDateDrawerOpen && (
					<ChangeZeroDateDrawer
						projectLocationActions={projectLocationActionCreators}
						projectActions={projectActionCreators}
						selectedProjectLocations={[{ id: selectedWaiver?.id }]}
						projectKey={projectKey}
						onCancel={onChangeZeroDateCancel}
						onComplete={onChangeZeroDateComplete}
					/>
				)}
				{isChangeProjectManagerDrawerOpen && (
					<ChangeProjectManagerDrawer
						projectLocationActions={projectLocationActionCreators}
						projectManagers={allProjectManagers}
						selectedProjectLocations={[{ id: selectedWaiver?.id }]}
						subscriptionId={subscriptionId}
						projectKey={projectKey}
						onCancel={onChangeManagerCancel}
						onComplete={onChangeManagerComplete}
						isLoading={isLoadingFormFilters}
					/>
				)}
				{showZeroDateWarning && (
					<Modal
						id='zeroDateWarning'
						title='Skipped Locations'
						body={zeroDateWarningModalBody}
						onCloseClick={() => setShowZeroDateWarning(false)}
						customFooter={zeroDateWarningModalFooter}
					/>
				)}
				{showChangeManagerWarning && (
					<Modal
						id='managerWarning'
						title='Skipped Locations'
						body={managerWarningModalBody}
						onCloseClick={() => setShowChangeManagerWarning(false)}
						customFooter={managerWarningModalFooter}
					/>
				)}
			</LoadingIndicator>
		</div>
	);
};

export default ProjectWaivers;
